import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Attaching a CG order that has no deal.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Kyu, 1 Sep: "if we delete a deal, CG order should not be affected. BUT we
 * should be able to have the ability to create a new deal AND associate that
 * new deal to a CG order - as long as that CG order has no associated deal."
 *
 * `cg_orders.deal_id` is ON DELETE SET NULL, so deleting a deal already leaves
 * the order standing with no deal. That was the state I originally proposed
 * BLOCKING the delete to avoid — wrongly, because blocking it means a
 * duplicate deal that happens to carry an order can never be removed, and
 * duplicates are the thing being cleaned up.
 *
 * With a way back, the unlinked order is a recoverable state rather than
 * damage. This is the way back.
 *
 * ── Everything goes through the RPC, nothing touches the table ────────────
 *
 * `cg_orders` sits behind two gates — a permissive policy on `is_cg_member()`
 * and a RESTRICTIVE module gate on `tap_may_reach_cg_orders()`. Somebody
 * working the Deals board may hold neither, so a plain `.update()` from here
 * is refused by RLS and reads to them as a broken button.
 *
 * `link_order_to_deal` (20270101000100) is SECURITY DEFINER and checks both
 * gates itself, and it puts the "has no deal yet" guard in the UPDATE's own
 * WHERE clause so two people clicking at once cannot steal each other's order.
 */

export interface UnlinkedOrder {
  id: string;
  orderNo: string;
  client: string;
  status: string;
}

/**
 * The orders available to attach: those with no deal at all.
 *
 * Filtered in the QUERY, not in the component. A component that fetched every
 * order and hid the taken ones would show an empty list to anybody whose RLS
 * lets them see nothing, and a full one to an admin — with the actual rule
 * living in a `.filter()` nobody tests.
 */
export async function fetchUnlinkedOrders(client: SupabaseClient): Promise<UnlinkedOrder[]> {
  const { data, error } = await client
    .from('cg_orders')
    .select('id, order_no, client, status')
    .is('deal_id', null)
    .order('order_no', { ascending: false })
    .limit(200);
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    id: String(row.id),
    orderNo: String(row.order_no),
    client: String(row.client),
    status: String(row.status),
  }));
}

/**
 * `enabled` so the list is not fetched until somebody opens the picker.
 *
 * Most people opening a deal drawer are not attaching an order, and this is a
 * query against a table many of them cannot read — firing it every time would
 * put an RLS refusal in the console on every drawer open and train everyone to
 * ignore the console.
 */
export function useUnlinkedOrders(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['cg', 'orders', 'unlinked'],
    queryFn: () => fetchUnlinkedOrders(client),
    enabled,
    retry: false,
  });
}

export function useLinkOrderToDeal() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; dealId: string }) => {
      const { data, error } = await client.rpc('link_order_to_deal', {
        p_order: input.orderId,
        p_deal: input.dealId,
      });
      if (error) {
        throw new Error(error.message);
      }
      // Typed Json by the generated types; the caller does not read it — the
      // invalidation below is what updates the screen.
      return data;
    },
    onSettled: () => {
      // Both sides move: the order now has a deal, and the deal now has an
      // order. Invalidating only one leaves the other screen stale until a
      // refresh, which is how somebody ends up attaching it twice.
      void queryClient.invalidateQueries({ queryKey: ['cg'] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/**
 * The database's refusals, in words somebody can act on.
 *
 * The two permission messages stay separate: "you are not on the CG team" and
 * "your role does not include CG Orders" send somebody to different people,
 * and collapsing them into "no access" wastes a round trip through whoever
 * they ask first.
 */
export function linkOrderErrorMessage(raw: string): string {
  if (raw.includes('order_already_has_a_deal')) {
    return 'That order was attached to a deal while this list was open. Reopen the picker to see what is still free.';
  }
  if (raw.includes('order_not_found')) {
    return 'That order no longer exists.';
  }
  if (raw.includes('deal_not_found')) {
    return 'This deal no longer exists.';
  }
  if (raw.includes('not_authorized_for_cg_orders')) {
    return 'Only the CG team can attach an order to a deal.';
  }
  if (raw.includes('cg_orders_module_denied')) {
    return 'Your role does not include CG Orders, so you cannot attach one.';
  }
  if (raw.includes('not_signed_in')) {
    return 'You are signed out. Sign in and try again.';
  }
  return 'Could not attach that order. Nothing was changed.';
}
